import type { Snapshot } from '../api/types';

export interface FlowState {
  pvActive: boolean;
  gridImporting: boolean;
  gridExporting: boolean;
  battCharging: boolean;
  battDischarging: boolean;
  loadActive: boolean;
  backupActive: boolean;
}

export function deriveFlowState(s: Partial<Snapshot>): FlowState {
  const meter = s.meter_total_power_w ?? 0;
  // Erdotex: CT alleen op de accutak. Die meet precies de stroom tussen omvormer
  // en net/gebouw, dus de pijl omvormer<->Grid volgt de meter gewoon; alleen het
  // netsaldo van de aansluiting (Grid-bol) en het verbruik zijn onbekend, dus
  // geen Verbruik-pijl, ook niet via household_load_w.
  const verbruikOnbekend = !!s._verbruik_onmeetbaar;
  // Is het sitetotaal niet vastgesteld (_battery_total_power_known ===
  // false), dan is het getal een terugval op één bank of een nul zonder data.
  // Op VJ wisselen de banken elkaar af, dus ook de richting van één bank zegt
  // niets over de site: dan geen accupijl (wachtrij #200).
  const battResolved = s._battery_total_power_known !== false;
  const batt = battResolved ? (s._battery_total_power_w ?? s.battery_power_w ?? 0) : 0;
  // PV: zelfde prioriteit als pvW in FlowSVG — het sitetotaal (master + slave
  // op VJ) eerst. Op master-only PV verdween de pijl terwijl het getal ernaast
  // opwek van de slave toonde (wachtrij #535).
  const pv = s._pv_total_power_w ?? s.pv_power_w ?? 0;
  return {
    pvActive: pv > 100,
    gridExporting: meter > 50,
    gridImporting: meter < -50,
    battCharging: batt > 100,
    battDischarging: batt < -100,
    // loadActive: gebruik grid_port_load_w (server-side residu excl. backup) als dat
    // aanwezig is — zelfde prioriteit als loadW in FlowSVG. Fallback: household_load_w.
    // Onbekend verbruik is GEEN actieve last: we tekenen liever geen pijl dan
    // een pijl die we niet kunnen onderbouwen. `?? 0` doet hier hetzelfde als
    // `?? null` (beide leveren false), maar de null maakt de bedoeling expliciet.
    loadActive: !verbruikOnbekend && (s.grid_port_load_w ?? s.household_load_w ?? 0) > 100,
    backupActive: (s._backup_load_total_w ?? s.backup_load_w ?? 0) > 100,
  };
}

// Dagverbruik voor de eigenverbruik-tegel. Bron is de omvormerteller 33179
// `load_today_kwh`, niet de som van 33586 (grid-port) en 33596 (backup).
// Gemeten in state.db op 2026-09-21 (wachtrij #201): op VJ is die som gelijk
// aan 33179 (0,7 + 96,3 = 97,0 kWh — geen dubbeltelling, 33596 is daar het
// sitetotaal), maar op NS gaf de som 6,0 tegen 18,2 kWh en op HO 40,3 tegen
// 47,0 kWh. De energiebalans (PV + import + ontlading − lading − export)
// sluit op beide sites op 33179, binnen de conversieverliezen. De som blijft
// alleen terugval voor een snapshot zonder 33179.
export function dagverbruikKwh(s: Partial<Snapshot> | null | undefined): number | null {
  if (!s) return 0;
  // CT alleen op de accutak (Erdotex): geen dagverbruik bekend. Niet terugvallen
  // op 33179, die ziet de PV op de andere omvormers niet.
  if (s._verbruik_onmeetbaar === 'meter_omvormertak') return null;
  // AC-gekoppelde PV: 33179 ziet PV op een andere omvormer niet; de backend
  // zet dan _load_total_today_kwh uit de balans van dagtellers (alleen daar).
  if (typeof s._load_total_today_kwh === 'number') return s._load_total_today_kwh;
  if (typeof s.load_today_kwh === 'number') return s.load_today_kwh;
  return (s.household_load_today_kwh ?? 0) + (s.backup_load_today_kwh ?? 0);
}

// Dagregel onder de Verbruik-pil: het deel van het dagverbruik dat NIET via de
// backup-poort liep, zodat het past bij de live waarde grid_port_load_w (residu
// excl. backup) en niet dubbel telt met de Backup-pil. Bron is 33179 (Grid Load
// + Back-Up Load, sluit de energiebalans, zie hierboven) min 33596 (backup).
// Teller 33586 ("Grid Load daily") leest op NS en HO vrijwel nul terwijl daar
// wél verbruik is (wachtrij #940, state.db 21-09 21:54 UTC: NS 3,8 tegen 18,2 −
// 2,2 = 16,0 kWh; HO 0,0 tegen 47,7 − 41,0 = 6,7 kWh). 33586 blijft alleen
// terugval voor een snapshot zonder 33179.
export function verbruikGridpoortVandaagKwh(s: Partial<Snapshot> | null | undefined): number {
  if (!s) return 0;
  if (typeof s._load_total_today_kwh === 'number') {
    return Math.max(0, s._load_total_today_kwh - (s.backup_load_today_kwh ?? 0));
  }
  if (typeof s.load_today_kwh === 'number') {
    return Math.max(0, s.load_today_kwh - (s.backup_load_today_kwh ?? 0));
  }
  return s.household_load_today_kwh ?? 0;
}
